import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n/LanguageProvider';

const Hero: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-navy via-slate-800 to-navy">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-sky/20 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-sand/30 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-sky/10 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
        {/* Extra gradient sheen for a more exciting hero */}
        <div className="absolute left-1/2 -top-8 -translate-x-1/2 w-3/4 h-72 bg-gradient-to-r from-sky/20 via-sky/40 to-sand/0 rounded-full blur-3xl opacity-60 animate-gradient-shine"></div>
      </div>
      
      {/* Floating Geometric Shapes */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-1/4 w-4 h-4 bg-sky/40 rotate-45 animate-float"></div>
        <div className="absolute top-1/3 right-1/4 w-6 h-6 bg-sand/40 rounded-full animate-float animation-delay-1000"></div>
        <div className="absolute bottom-1/3 left-1/6 w-3 h-3 bg-sky/30 animate-float animation-delay-2000"></div>
        <div className="absolute bottom-20 right-1/3 w-5 h-5 bg-sand/30 rotate-12 animate-float animation-delay-3000"></div>
      </div>
      
      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDMpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-40"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-5xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-sky/10 border border-sky/20 backdrop-blur-sm mb-8 animate-fade-in-up">
            <span className="w-2 h-2 bg-sky rounded-full mr-2 animate-pulse"></span>
            <span className="text-sand/90 text-sm font-medium">{t('hero.badge')}</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sand via-sky to-sand mb-8 font-space leading-tight animate-fade-in-up animation-delay-200">
            {t('hero.titleLine1')}
            <span className="block text-sky animate-fade-in-up animation-delay-400">{t('hero.titleLine2')}</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-sand/80 mb-12 leading-relaxed max-w-4xl mx-auto animate-fade-in-up animation-delay-600">
            {t('hero.subtitle1')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center animate-fade-in-up animation-delay-800">
            <Link 
              to="/contact#calendly"
              className="group relative bg-gradient-to-r from-sky to-sky/80 text-navy px-10 py-5 rounded-full font-semibold text-lg hover:shadow-2xl hover:shadow-sky/25 transition-all duration-300 transform hover:-translate-y-1 inline-flex items-center gap-2"
            >
              <span>{t('hero.ctaPrimary')}</span>
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            
            <a 
              href="#services"
              className="text-sand/90 font-semibold text-lg hover:text-sky transition-colors duration-200 flex items-center gap-2 group"
            >
              <span>{t('hero.ctaSecondary')}</span>
              <svg className="w-5 h-5 group-hover:translate-y-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </a>
          </div>
        </div>
      </div>
      
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-sand/40 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-sand/60 rounded-full mt-2 animate-scroll"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
